<script setup>
import InfoButton from "@/components/buttons/InfoButton.vue";
import {computed, ref} from "vue";
import CraftingAnswers from "@/components/gamification/question/CraftingAnswers.vue";
import {useStore} from "vuex";
import Loading from "@/components/Loading.vue";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const store = useStore()

const props = defineProps(['questionId','showDialog','subjectId'])
const emit = defineEmits(['close'])

const loading = ref(false)
const questionCopy = ref(null)

const isNew = computed(() => !props.questionId)
const questionTypes = computed(()=> store.getters['Questions/getQuestionTypes'])
const question = computed(()=>store.getters['Questions/getQuestion'])
const typeOfQuestionsItems = computed(() => {
  return Object.values(store.getters['Questions/getQuestionTypes']).map((qt)=>{
    return {
      ...qt,
      title: t(qt.locKey)
    }
  })
})
const isOpen = computed({
  get: () => props.showDialog,
  set: (value) => {
    if (!value) {
      emit('close')
      onAfterLeave()
    }
  }
})
const invalidAnswers = computed(()=> store.getters['Answers/invalid'])
const invalidQuestion = computed(()=> !question.value.text || question.value.text.length === 0)

const required_rule = value => !!value || t('gamification.required')

function onClose(){
  const args = {
    questionName: store.getters['Questions/getQuestion'].text,
  }
  emit('close',args)
  onAfterLeave()
}

async function onAfterEnter(){
  loading.value = true
  if(isNew.value){
    if(store.getters['Questions/getQuestion'].answers.length === 0){
      store.dispatch('Answers/pushClearAnswer', -1)
      store.dispatch('Answers/pushClearAnswer', -2)
      store.commit('Questions/setAnswersToQuestion', store.getters['Answers/getAnswers'])
    }
  } else {
    await store.dispatch('Questions/fetchQuestionById', props.questionId)
    store.commit('Answers/setAnswers', store.getters['Questions/getQuestion'].answers)
    questionCopy.value = JSON.parse(JSON.stringify(store.getters['Questions/getQuestion']))
  }
  loading.value = false
}

function onAfterLeave(){
  store.dispatch('Questions/clearQuestion')
  store.dispatch('Answers/clearAnswers')
}

async function onSave(){
  loading.value = true
  if (isNew.value){
    await create()
    onClose()
  } else {
    if(questionCopy.value){
      let toDelete = []
      let toUpdate = []
      let toCreate = []
      if(questionCopy.value.type !== store.getters['Questions/getQuestion'].type || questionCopy.value.text !== store.getters['Questions/getQuestion'].text){
        await updateQuestion()
      }
      if(questionCopy.value.type !== questionTypes.value.TrueFalse.value && store.getters['Questions/getQuestion'].type === questionTypes.value.TrueFalse.value){
        const answerRequestBodies = makeYesNoRequestBodies(props.questionId)
        for (const rb of answerRequestBodies){
          await store.dispatch('Answers/createAnswer', {item: rb})
        }
        toDelete = questionCopy.value.answers
      } else {
        store.getters['Answers/getAnswers'].forEach(answer => {
          if(answer.toDelete){
            toDelete.push(answer)
          } else if(Number(answer.id) < 0){
            toCreate.push({...answer, questionId: props.questionId})
          } else {
            const copy = questionCopy.value.answers.find(a => a.id === answer.id)
            if(copy){
              if(copy.text !== answer.text || copy.correct !== answer.correct){
                toUpdate.push(answer)
              }
            }
          }
        })
      }
      await updateAnswers(toUpdate)
      await deleteAnswers(toDelete)
      await createAnswers(toCreate)
      onClose()
    }
  }
}

async function create(){
  const questionData = {
    ...store.getters['Questions/getQuestion'],
    subjectId: props.subjectId
  }
  const questionId = await store.dispatch('Questions/createQuestion', {item: questionData})
  const displayedAnswers = store.getters['Answers/displayedAnswers']
  if(questionData.type === questionTypes.value.TrueFalse.value){
    const answerRequestBodies = makeYesNoRequestBodies(questionId)
    for (const rb of answerRequestBodies){
      await store.dispatch('Answers/createAnswer', {item: rb})
    }
  } else {
    displayedAnswers.forEach(a => a.questionId = questionId)
    await createAnswers(displayedAnswers)
  }
}

function makeYesNoRequestBodies(questionId){
  // because of localization, we will send text in cz only
  // temporary ID for yes answer is -1
  const displayedAnswers = store.getters['Answers/displayedAnswers']
  const yesAnswer = displayedAnswers.find(a => a.id === -1)
  return [
    {text: 'Ano', questionId: questionId, correct: yesAnswer.correct},
    {text: 'Ne', questionId: questionId, correct: !yesAnswer.correct}
  ]
}


async function updateQuestion(){
  await store.dispatch('Questions/updateQuestion',{item: store.getters['Questions/getQuestion']})
}
async function updateAnswers(answers){
  for (const a of answers){
    await store.dispatch('Answers/updateAnswer', {item: a})
  }
}

async function deleteAnswers(answers){
  for (const a of answers){
    await store.dispatch('Answers/deleteAnswerById', a.id)
  }
}
async function createAnswers(answers){
  for (const a of answers){
    await store.dispatch('Answers/createAnswer', {item: a})
  }
}

</script>

<template>
  <v-dialog
      max-width="1200px"
      v-model="isOpen"
      @after-enter="onAfterEnter"
      @after-leave="onAfterLeave"
    >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center py-2">
        <div>
          {{isNew ? $t('gamification.new_question') : $t('gamification.test_question')}}
        </div>
        <div class="d-flex align-center">
          <InfoButton large>{{$t('gamification.question_help_text')}}</InfoButton>
          <v-btn @click="onClose" icon="close" variant="text" />
        </div>
      </v-card-title>

    <hr class="hor-line">
      <v-card elevation="0" class="px-5 py-5" id="inner-card">
        <div class="h-75" v-if="loading">
          <Loading />
        </div>
        <div v-else>
          <div class="d-flex ga-5">
            <v-select
                class="w-25"
                :label="$t('gamification.type_of_question')"
                color="#78BE13"
                variant="underlined"
                :items="typeOfQuestionsItems"
                v-model="store.getters['Questions/getQuestion'].type"
            />
            <v-text-field
                v-model="store.getters['Questions/getQuestion'].text"
                class="w-75"
                :label="$t('gamification.question_text')"
                :rules="[required_rule]"
                color="#78BE13"
                tabindex="0"
                variant="underlined"
            />
          </div>
          <h3 class="font-weight-medium pt-1 pb-3"> {{ $t('gamification.options') }} </h3>

          <crafting-answers
              :is-new="isNew"
          />
        </div>
      </v-card>
      <hr class="hor-line">
      <div v-if="!loading" style="height: 64px" class="d-flex align-center justify-end px-5">
        <v-btn @click="onSave" :disabled="invalidAnswers || invalidQuestion" color="primary" rounded>
          {{$t('entities.save')}}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>

.hor-line {
  border: 0.8px solid #eaeaea;
}

#inner-card {
  border-radius: 14px;
  max-height: 62vh;
  overflow-y: auto;
}

</style>